"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getQuotationListReturnPath } from "./quotationListState";
import axios from "axios";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowLeft, Package, User, FileText, Clock, CheckCircle2, Circle,
  Loader2, Send, ChevronRight, ChevronLeft, FileDown, ArrowRight,
  MapPin, Phone, Building2, CreditCard, Hash, Calendar, Truck,
  Receipt, TrendingUp, Pencil, X,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { API_BASE_URL } from "../../../../src/App.js";
import { QuotationTemplate } from "./QuotationTemplate";
import { downloadPDF } from "../../../utils/pdfGenerator";
import { OrderForm } from "./OrderForm";
import { getOrderDeliveryText } from "./orderDeliveryText";
import { getOrderRevisionBumpIntent } from "../../../utils/orderRevisionIntent";
import {
  getQuotationSignatory,
  formatFreightChargesLineLabel,
  getFreightNotesDisplay,
  formatIndiaDateTime,
} from "./quotationPdfShared";

const formatDate = (d: any) => {
  if (!d) return "N/A";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "N/A";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" }).replace(/\//g, "-");
};

const formatDateTime = (d: any) => {
  return formatIndiaDateTime(d) || "N/A";
};

const TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "products", label: "Quotation Details", icon: Package },
  { id: "notes", label: "Notes", icon: FileText },
];

export const OrderDetailPage: React.FC = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authToken = "", id = "", role = "", username = "" } = useSelector((state: any) => state.loginData || {});
  const [order, setOrder] = useState<any>(null);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingNote, setAddingNote] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [editingOrder, setEditingOrder] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/orders?firstResults=0&maxResults=500`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const allOrders = Array.isArray(res.data?.orders) ? res.data.orders
        : Array.isArray(res.data?.Orders) ? res.data.Orders : [];
      const found = allOrders.find((o: any) => String(o.id) === String(orderId));
      setOrder(found || null);
    } catch { toast.error("Failed to load order"); }
  }, [authToken, id, orderId]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order-statuses`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data?.data || res.data?.orderStatuses || res.data?.OrderStatuses || [];
      setStatuses(Array.isArray(data) ? [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)) : []);
    } catch {}
  }, [authToken, id]);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order/${orderId}/notes`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data?.notes || res.data || [];
      setNotes(Array.isArray(data) ? data : []);
    } catch {}
  }, [authToken, id, orderId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchOrder(), fetchStatuses(), fetchNotes()]).finally(() => setLoading(false));
  }, [fetchOrder, fetchStatuses, fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(
        `${API_BASE_URL}account/${id}/order/${orderId}/note/create`,
        { note: newNote.trim(), createdBy: username },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setNewNote("");
      await fetchNotes();
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
    finally { setAddingNote(false); }
  };

  const handleAdvanceStatus = async () => {
    if (!order || statuses.length === 0) return;
    const currentIdx = statuses.findIndex(
      (s) => s.label?.toLowerCase() === order.status?.toLowerCase()
    );
    const nextStatus = statuses[currentIdx + 1];
    if (!nextStatus) return;
    setUpdatingStatus(true);
    const bumpRevision = getOrderRevisionBumpIntent(order.id);
    try {
      await axios.put(
        `${API_BASE_URL}account/${id}/order/update`,
        {
          ...order,
          status: nextStatus.label,
          createdBy: username,
          revisedQuotation: bumpRevision,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchOrder();
      toast.success(`Status updated to ${nextStatus.label}`);
    } catch { toast.error("Failed to update status"); }
    finally { setUpdatingStatus(false); }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const settingsRes = await axios.get(`${API_BASE_URL}account/${id}/settings`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await downloadPDF(
        <QuotationTemplate order={order} settings={settingsRes.data} />,
        `Quotation_${order.quotationNumber || order.id}.pdf`
      );
    } catch { toast.error("Failed to generate PDF"); }
    finally { setDownloadingPdf(false); }
  };

  const handleChangeStatus = async (statusLabel: string) => {
    if (!order) return;
    setChangingStatus(true);
    const bumpRevision = getOrderRevisionBumpIntent(order.id);
    try {
      await axios.put(
        `${API_BASE_URL}account/${id}/order/update`,
        {
          ...order,
          status: statusLabel,
          createdBy: username,
          revisedQuotation: bumpRevision,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchOrder();
      toast.success(`Status changed to ${statusLabel}`);
    } catch { toast.error("Failed to change status"); }
    finally { setChangingStatus(false); }
  };

  const handleOpenEdit = () => {
    setEditingOrder(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-10 h-10 animate-spin text-[var(--sidebar-primary)]" />
        <p className="text-sm text-gray-500">Loading quotation...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-lg font-semibold text-gray-700">Quotation not found</p>
        <Button onClick={() => navigate(getQuotationListReturnPath())} className="btn-primary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Quotations
        </Button>
      </div>
    );
  }

  const currentStatusIndex = statuses.findIndex(
    (s) => s.label?.toLowerCase() === order.status?.toLowerCase()
  );
  const nextStatus = statuses[currentStatusIndex + 1];
  const currentStatus = statuses[currentStatusIndex];
  const products: any[] = order.products || [];

  const subTotal = parseFloat(order.price || "0");
  const freightCharges = parseFloat(order.freightCharges || order.transport || "0");
  const freightGstPct = parseFloat(String(order.freightChargesGst || "").trim()) || 0;
  const freightGstAmt = (freightCharges * freightGstPct) / 100;
  const freightInclGst = freightCharges + freightGstAmt;
  const productGst = products.reduce((sum: number, p: any) => {
    const base = parseFloat(p.price || "0") * parseFloat(p.nos || "1");
    return sum + (base * parseFloat(p.gst || "0")) / 100;
  }, 0);
  const savedProductGst = parseFloat(String(order.gst || "0").trim()) || 0;
  const gstOnProducts = savedProductGst > 0 ? savedProductGst : productGst;
  const computedGrand = subTotal + gstOnProducts + freightCharges + freightGstAmt;
  const savedTotal = parseFloat(String(order.total || "").trim());
  const grandTotal =
    Number.isFinite(savedTotal) && savedTotal > 0 ? savedTotal : computedGrand;

  const freightSummaryLabel =
    freightCharges > 0 ? formatFreightChargesLineLabel(freightCharges, freightGstPct) : "Freight charges";
  const freightSummaryValue =
    freightCharges > 0
      ? freightGstPct > 0 && freightGstAmt > 0
        ? `₹${freightInclGst.toFixed(2)}`
        : `₹${freightCharges.toFixed(2)}`
      : "N/A";
  const statusColor = currentStatus?.color || "#6366f1";
  const deliveryDisplay = getOrderDeliveryText(order) || "N/A";
  const { name: pdfSignatoryName, mobile: pdfSignatoryMobile } = getQuotationSignatory(order);

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* â”€â”€ Top Hero Banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="relative overflow-hidden rounded-2xl mb-6"
        style={{ background: `linear-gradient(135deg, ${statusColor}18 0%, ${statusColor}08 100%)`, border: `1px solid ${statusColor}30` }}>
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 -translate-y-12 translate-x-12"
          style={{ background: statusColor }} />

        <div className="relative p-5">
          {/* Back button row */}
          <button
            onClick={() => navigate(getQuotationListReturnPath())}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 transition mb-4 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            All Quotations
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-black tracking-tight text-gray-900">
                  {order.quotationNumber || `#${order.id}`}
                </h1>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold border"
                  style={{ backgroundColor: statusColor + "20", color: statusColor, borderColor: statusColor + "50" }}
                >
                  {currentStatus?.label || order.status || "New"}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                <span className="font-medium text-gray-700">{order.customerName}</span>
                {" "}&mdash; Created {formatDateTime(order.createdAt)} by <span className="font-medium">{order.createdBy}</span>
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadPDF}
                disabled={downloadingPdf}
                className="bg-white border-green-300 text-green-700 hover:bg-green-50 font-semibold shadow-sm"
              >
                {downloadingPdf
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <FileDown className="w-4 h-4 mr-1.5" />}
                Download PDF
              </Button>
              {nextStatus && (
                <Button
                  size="sm"
                  onClick={handleAdvanceStatus}
                  disabled={updatingStatus}
                  className="font-semibold shadow-sm text-white"
                  style={{ backgroundColor: nextStatus.color || "#6366f1" }}
                >
                  {updatingStatus
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <ArrowRight className="w-4 h-4 mr-1.5" />}
                  Move to {nextStatus.label}
                </Button>
              )}
            </div>
          </div>

          {/* Financial Summary Strip */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { icon: Receipt, label: "Sub Total", value: `₹${subTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, color: "#6366f1" },
              { icon: TrendingUp, label: "GST (products)", value: `₹${gstOnProducts.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, color: "#f59e0b" },
              { icon: CreditCard, label: "Grand Total", value: `₹${grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, color: "#10b981" },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="bg-white/80 backdrop-blur rounded-xl p-3 border border-white/60 shadow-sm">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</p>
                </div>
                <p className="text-sm sm:text-base font-black text-gray-900 truncate">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* â”€â”€ Tab Navigation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-2xl p-1.5 shadow-sm mb-6">
        {TABS.map((tab, idx) => {
          const Icon = tab.icon;
          const isActive = activeTab === idx;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(idx)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? "bg-[var(--sidebar-primary)] text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden text-xs">{tab.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </div>

      {/* â”€â”€ Tab Content â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}

      {/* TAB 0 — OVERVIEW */}
      {activeTab === 0 && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Status Progress + Change Status */}
          {statuses.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">Order Progress</h2>
                {/* Status Change Dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-medium">Change Status:</span>
                  <select
                    value={order.status || ""}
                    onChange={(e) => handleChangeStatus(e.target.value)}
                    disabled={changingStatus}
                    className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white font-semibold focus:outline-none focus:border-[var(--sidebar-primary)] disabled:opacity-50"
                    style={{ color: currentStatus?.color || "#6366f1" }}
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.label}>{s.label}</option>
                    ))}
                  </select>
                  {changingStatus && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </div>
              </div>
              <div className="flex items-center overflow-x-auto pb-2 gap-0">
                {statuses.map((s, idx) => {
                  const isCompleted = idx < currentStatusIndex;
                  const isCurrent = idx === currentStatusIndex;
                  const isActive = isCompleted || isCurrent;
                  const color = s.color || "#6366f1";
                  return (
                    <React.Fragment key={s.id || idx}>
                      <div className="flex flex-col items-center min-w-[72px]">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all shadow-sm"
                          style={{
                            backgroundColor: isActive ? color + "20" : "#f9fafb",
                            borderColor: isActive ? color : "#e5e7eb",
                            boxShadow: isCurrent ? `0 0 0 4px ${color}20` : "none",
                          }}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" style={{ color }} />
                          ) : isCurrent ? (
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                        <span
                          className="text-[9px] text-center mt-2 font-semibold leading-tight max-w-[64px]"
                          style={{ color: isActive ? color : "#d1d5db" }}
                        >
                          {s.label}
                        </span>
                      </div>
                      {idx < statuses.length - 1 && (
                        <div
                          className="flex-1 h-0.5 min-w-[16px] transition-all"
                          style={{ backgroundColor: idx < currentStatusIndex ? (statuses[idx].color || "#6366f1") : "#e5e7eb" }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              {nextStatus && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Next: <span className="font-semibold" style={{ color: nextStatus.color }}>{nextStatus.label}</span>
                  </p>
                  <Button
                    size="sm"
                    onClick={handleAdvanceStatus}
                    disabled={updatingStatus}
                    className="text-white text-xs font-semibold rounded-lg"
                    style={{ backgroundColor: nextStatus.color || "#6366f1" }}
                  >
                    {updatingStatus ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                    Advance to {nextStatus.label}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Quotation Details Grid */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">Quotation Details</h2>
              <div className="flex flex-col gap-2 sm:items-end">
                <p className="text-[11px] text-gray-500 text-left sm:text-right max-w-md">
                  While “Create next revision” is enabled, each edit save or status change advances R0 → R1 → R2…. Turn the checkbox off manually to stop.
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Button size="sm" variant="outline" onClick={handleOpenEdit}
                  className="text-blue-700 border-blue-300 hover:bg-blue-50 text-xs font-semibold">
                  <Pencil className="w-3.5 h-3.5 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadPDF} disabled={downloadingPdf}
                  className="text-green-700 border-green-300 hover:bg-green-50 text-xs font-semibold">
                  <FileDown className="w-3.5 h-3.5 mr-1" />
                  {downloadingPdf ? "Generating..." : "PDF"}
                </Button>
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: Hash, label: "Quotation No", value: order.quotationNumber || `#${order.id}`, accent: "#6366f1" },
                { icon: Calendar, label: "Created At", value: formatDateTime(order.createdAt), accent: "#8b5cf6" },
                { icon: User, label: "Created By", value: order.createdBy || "N/A", accent: "#ec4899" },
                { icon: Truck, label: "Delivery", value: deliveryDisplay, accent: "#f59e0b" },
                { icon: Clock, label: "Validity", value: order.validity || "N/A", accent: "#10b981" },
                { icon: FileText, label: "Subject", value: order.subject || "N/A", accent: "#3b82f6" },
                {
                  icon: User,
                  label: "Kind Attention",
                  value:
                    order.kindAttention ||
                    `${order.person_name_ || ""}${order.person_name_ && order.person_mobile_ ? " / " : ""}${order.person_mobile_ || ""}` ||
                    "N/A",
                  accent: "#2563eb",
                },
                { icon: Receipt, label: "Sub Total (pre-GST)", value: `₹${subTotal.toFixed(2)}`, accent: "#6366f1" },
                { icon: TrendingUp, label: "GST on products", value: `₹${gstOnProducts.toFixed(2)}`, accent: "#f59e0b" },
                { icon: Truck, label: freightSummaryLabel, value: freightSummaryValue, accent: "#06b6d4" },
                { icon: CreditCard, label: "Grand Total", value: `₹${grandTotal.toFixed(2)}`, accent: "#10b981" },
                { icon: User, label: "PDF signatory", value: pdfSignatoryName || "N/A", accent: "#0ea5e9" },
                { icon: Phone, label: "PDF signatory mobile", value: pdfSignatoryMobile || "N/A", accent: "#14b8a6" },
              ].map(({ icon: Icon, label, value, accent }) => (
                <div key={label} className="rounded-xl p-3.5 border border-gray-100 hover:border-gray-200 transition group"
                  style={{ background: `linear-gradient(135deg, ${accent}06 0%, transparent 100%)` }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full sm:h-8 sm:w-8"
                      style={{ backgroundColor: `${accent}24` }}
                    >
                      <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" style={{ color: accent }} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-800 break-words">{value}</p>
                </div>
              ))}
            </div>

            <div className="px-5 pb-5 space-y-4">
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-2">
                  Freight Notes
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {getFreightNotesDisplay(order)}
                </p>
                <p className="text-[11px] text-gray-500 mt-2">
                  Shown on PDF under Terms &amp; Conditions — 3. Freight Charges
                </p>
              </div>
              {order.terms && (
                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-2">
                    Payment Terms
                  </p>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{order.terms}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 1 â€” PRODUCTS & CUSTOMER */}
      {activeTab === 1 && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Customer Card */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800">{order.customerName || "Customer"}</h2>
                  <p className="text-[11px] text-gray-500">{order.company_name_ || ""}</p>
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: Building2, label: "Company", value: order.company_name_, color: "#3b82f6" },
                { icon: MapPin, label: "Address", value: order.address_, color: "#10b981" },
                { icon: Phone, label: "Mobile", value: order.mobile, color: "#8b5cf6" },
                { icon: User, label: "Contact Person", value: order.person_name_, color: "#ec4899" },
                { icon: Phone, label: "Person Mobile", value: order.person_mobile_, color: "#f59e0b" },
                { icon: Hash, label: "GSTIN", value: order.gst_, color: "#6366f1" },
                { icon: MapPin, label: "State", value: order.state_, color: "#06b6d4" },
              ].filter(f => f.value).map(({ icon: Icon, label, value, color }) => (
                <div key={label} className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full mt-0.5 sm:h-7 sm:w-7"
                    style={{ backgroundColor: color + "18" }}
                  >
                    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5 break-words">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Package className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800">Products</h2>
                  <p className="text-[11px] text-gray-500">{products.length} item{products.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["#", "Product", "Article No", "HSN", "Qty", "Unit", "Price", "GST", "Amount"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p: any, i: number) => {
                    const price = parseFloat(p.price || "0");
                    const nos = parseFloat(p.nos || "1");
                    const gstRate = parseFloat(p.gst || "0");
                    const amount = price * nos;
                    return (
                      <tr key={i} className="border-t border-gray-100 hover:bg-violet-50/30 transition-colors">
                        <td className="px-4 py-3.5 text-gray-400 font-medium text-xs">{i + 1}</td>
                        <td className="px-4 py-3.5 max-w-[180px]">
                          <p className="font-bold text-gray-900 truncate">{p.name || "N/A"}</p>
                          {p.description && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{p.description}</p>}
                        </td>
                        <td className="px-4 py-3.5 text-gray-500 text-xs font-mono">{p.article_no || "-"}</td>
                        <td className="px-4 py-3.5 text-gray-500 text-xs font-mono">{p.hsn_code || "-"}</td>
                        <td className="px-4 py-3.5 font-semibold text-gray-700">{nos}</td>
                        <td className="px-4 py-3.5 text-gray-500 text-xs">{p.unit || p.area_uom || "-"}</td>
                        <td className="px-4 py-3.5 text-gray-700 font-medium">₹{price.toFixed(2)}</td>
                        <td className="px-4 py-3.5">
                          <span className="px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 text-[11px] font-bold">
                            {gstRate}%
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-black text-gray-900">₹{amount.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gradient-to-r from-violet-50 to-purple-50">
                    <td colSpan={7} className="px-4 py-4 text-right">
                      <div className="flex flex-col items-end gap-1 text-xs text-gray-600">
                        <span>Sub Total: <span className="font-bold text-gray-800">₹{subTotal.toFixed(2)}</span></span>
                        <span>
                          GST (products):{" "}
                          <span className="font-bold text-amber-600">₹{gstOnProducts.toFixed(2)}</span>
                        </span>
                        {freightCharges > 0 && (
                          <span>
                            {freightSummaryLabel}:{" "}
                            <span className="font-bold text-gray-800">{freightSummaryValue}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right text-xs font-bold text-gray-600">Grand Total</td>
                    <td className="px-4 py-4 font-black text-lg text-violet-700 whitespace-nowrap">
                      ₹{grandTotal.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2 â€” NOTES */}
      {activeTab === 2 && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-pink-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800">Notes & Activity</h2>
                  <p className="text-[11px] text-gray-500">{notes.length} note{notes.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-3">
              {notes.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-400">No notes yet. Be the first to add one.</p>
                </div>
              )}
              {notes.map((n: any, i: number) => {
                const colors = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#3b82f6", "#8b5cf6"];
                const color = colors[i % colors.length];
                const initials = (n.createdBy || n.created_by || "?").substring(0, 2).toUpperCase();
                return (
                  <div key={n.id || i} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-black mt-0.5"
                      style={{ backgroundColor: color }}>
                      {initials}
                    </div>
                    <div className="flex-1 rounded-2xl border border-gray-100 bg-gray-50/50 p-3.5">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="text-xs font-bold text-gray-800">{n.createdBy || n.created_by || "Unknown"}</span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1 shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(n.createdAt || n.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{n.note}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Note */}
            <div className="px-5 pb-5 pt-0">
              <div className="rounded-2xl border-2 border-dashed border-gray-200 p-4 bg-gray-50/30">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Add Note</p>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Write your note here..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--sidebar-primary)]/30 focus:border-[var(--sidebar-primary)]/50 bg-white transition"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="btn-primary rounded-xl text-sm font-semibold px-5"
                  >
                    {addingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Add Note
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€ Tab Nav Footer (Prev/Next) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 mt-6 flex items-center justify-between shadow-sm rounded-t-2xl">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setActiveTab((t) => Math.max(0, t - 1))}
          disabled={activeTab === 0}
          className="rounded-xl font-semibold"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>

        <div className="flex gap-1.5">
          {TABS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                activeTab === idx ? "w-6 bg-[var(--sidebar-primary)]" : "bg-gray-300"
              }`}
            />
          ))}
        </div>

        <Button
          size="sm"
          onClick={() => setActiveTab((t) => Math.min(TABS.length - 1, t + 1))}
          disabled={activeTab === TABS.length - 1}
          className="btn-primary rounded-xl font-semibold"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* ── Edit Quotation with Full OrderForm ───────────────────────────────────────────── */}
      <OrderForm
        open={editingOrder}
        onClose={() => setEditingOrder(false)}
        refresh={() => fetchOrder()}
        userRole={role as "admin" | "dealer" | "employee"}
        userId={id}
        existingOrder={order}
      />
    </div>
  );
};

